import { useCatalogues } from "@sushicorp/contexts";
import { useShoppingCart } from "@sushicorp/contexts";
import { sanitizeQueryParams } from "@sushicorp/utils";
import { removeQueryParam } from "@sushicorp/utils";
import { getShoppingCart, deleteShoppingCart } from "artisn/shopping-cart";
import { createShoppingCart, mergeShoppingCart } from "artisn/shopping-cart";
import { useRouter } from "next/router";
import React, { useMemo } from "react";

import Styles from "./ShareCartModal.styles";
import { ShareCartModalProps as Props } from "./ShareCartModal.types";
import InfoActionModal from "../InfoActionModal/InfoActionModal";
import CONSTANTS from "config/constants";
import useAuth from "contexts/auth/auth.context.hooks";
import useShippingCost from "hooks/useShippingCost";

import CartErrorSVG from "../../../../public/assets/images/cart-error.svg";

const { ACCOUNT_ID, SHOPPING_CART_DEFAULT_NAME } = CONSTANTS.ARTISN;

const ShareCartModal: React.FC<Props> = props => {
  const { query, asPath, replace } = useRouter();
  const { catalogueId } = useCatalogues()?.selectedCatalogue ?? {};
  const { shoppingCart } = useShoppingCart();
  const { shareId } = sanitizeQueryParams(query);
  const { name: shoppingCartName = SHOPPING_CART_DEFAULT_NAME } =
    shoppingCart ?? {};
  const newPath = useMemo(() => removeQueryParam(asPath, "shareId"), [asPath]);
  const shippingCost = useShippingCost();
  const { isAnonymous = false, uid } = useAuth();

  const replaceShoppingCart = async () => {
    if (!shareId) return;
    if (!uid) return;

    const incomingCart = await getShoppingCart({
      shoppingCartName: SHOPPING_CART_DEFAULT_NAME,
      accountId: ACCOUNT_ID,
      customerId: shareId,
      anonymous: isAnonymous
    });

    if (!incomingCart) {
      await replace(newPath, undefined, { shallow: true });
      return;
    }

    if (shoppingCartName) {
      await deleteShoppingCart({
        shoppingCartName,
        anonymous: isAnonymous,
        accountId: ACCOUNT_ID,
        customerId: uid
      });
    }

    await createShoppingCart(
      {
        anonymous: isAnonymous,
        accountId: ACCOUNT_ID,
        customerId: uid
      },
      {
        stores: incomingCart.stores,
        channelId: +catalogueId,
        shippingCost,
        name: shoppingCartName
      }
    );
    await replace(newPath, undefined, { shallow: true });
  };

  const localMergeShoppingCart = async () => {
    if (!shareId) return;
    if (!uid) return;

    const incomingCart = await getShoppingCart({
      shoppingCartName: SHOPPING_CART_DEFAULT_NAME,
      customerId: shareId,
      anonymous: isAnonymous,
      accountId: ACCOUNT_ID
    });

    if (!incomingCart) {
      await replace(newPath, undefined, { shallow: true });
      return;
    }

    await mergeShoppingCart(incomingCart, {
      shoppingCartName: SHOPPING_CART_DEFAULT_NAME,
      anonymous: isAnonymous,
      accountId: ACCOUNT_ID,
      customerId: uid
    });

    await replace(newPath, undefined, { shallow: true });
  };

  return (
    <Styles className="ShareCartModal">
      <InfoActionModal
        title="¿Deseas reemplazar tu carrito anterior?"
        description="Estas creando un nuevo carrito, deseas mantener el que tenias guardado anteriormente"
        cancelText="Mantener ambos"
        confirmText="Crear nuevo carrito"
        confirmAction={replaceShoppingCart}
        cancelAction={localMergeShoppingCart}
        opened={!!shareId && !!shoppingCart}
        icon={<CartErrorSVG />}
      />
    </Styles>
  );
};

ShareCartModal.defaultProps = {};

export default ShareCartModal;
